import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery } from 'react-query'
import Card, { CardHeader, CardTitle, CardContent } from '../UI/Card'
import Button from '../UI/Button'
import LoadingSpinner from '../UI/LoadingSpinner'
import { X, Shield, TestTube, CheckCircle, AlertCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import api from '../../services/api'
import { organizationsAPI } from '../../services/organizationsAPI'

export default function SSOConfigurationModal({ isOpen, onClose, organizationId, ssoConfig, onSuccess }) {
  const [provider, setProvider] = useState('saml')
  const [isTesting, setIsTesting] = useState(false)
  const [testResult, setTestResult] = useState(null)

  const { register, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm({
    defaultValues: {
      provider: 'saml',
      oidc_provider: '',
      entity_id: '',
      sso_url: '',
      certificate: '',
      client_id: '',
      client_secret: '',
      password: '',
      confirm_password: '',
      issuer_url: '',
      scopes: ['openid', 'profile', 'email'],
      auto_provision: false,
      require_sso: false,
      default_role_id: '',
      is_active: true,
      email_domain: ''
    }
  })

  // Fetch product roles for default role selection
  const { data: rolesData } = useQuery(
    ['product-roles', organizationId],
    () => api.get(`/product-roles/product/${organizationId}`).then(r => r.data),
    { enabled: !!organizationId }
  )
  const roles = rolesData?.data?.roles || []

  // Fetch existing SSO configurations to guard against duplicates (org + provider unique)
  const { data: existingConfigsData, refetch: refetchExistingConfigs } = useQuery(
    ['sso-configurations-existing', organizationId],
    () => api.get(`/sso-configurations/organization/${organizationId}`).then(r => r.data),
    { enabled: !!organizationId, refetchOnMount: true }
  )
  const existingConfigurations = existingConfigsData?.data?.configurations || []

  const { data: orgResponse } = useQuery(
    ['organization', organizationId],
    () => organizationsAPI.getById(organizationId),
    { enabled: !!organizationId && isOpen }
  )
  const organization = orgResponse?.data?.data?.organization || null
  const existingSsoDomain = organization?.sso_email_domain || ''
  const isDomainLocked = Boolean(existingSsoDomain)

  // Reset form when modal opens or config changes
  useEffect(() => {
    if (isOpen) {
      if (ssoConfig) {
        // Determine if it's OIDC and extract the provider
        const isOidc = ssoConfig.provider && ssoConfig.provider !== 'saml'
        const oidcProvider = isOidc ? ssoConfig.provider : ''
        const providerType = isOidc ? 'oidc' : 'saml'
        
        reset({
          provider: providerType,
          oidc_provider: oidcProvider,
          entity_id: ssoConfig.entity_id || '',
          sso_url: ssoConfig.sso_url || '',
          certificate: ssoConfig.certificate || '',
          client_id: ssoConfig.client_id || '',
          client_secret: ssoConfig.client_secret || '',
          password: '', // Passwords are not stored, so always empty when editing
          confirm_password: '',
          issuer_url: ssoConfig.issuer_url || '',
          scopes: ssoConfig.scopes || ['openid', 'profile', 'email'],
          auto_provision: ssoConfig.auto_provision || false,
          require_sso: ssoConfig.require_sso || false,
          default_role_id: ssoConfig.default_role_id || '',
          is_active: ssoConfig.is_active !== false,
          email_domain: existingSsoDomain
        })
        setProvider(providerType)
      } else {
        // Reset to defaults for new configuration
        reset({
          provider: 'saml',
          oidc_provider: '',
          entity_id: '',
          sso_url: '',
          certificate: '',
          client_id: '',
          client_secret: '',
          password: '',
          confirm_password: '',
          issuer_url: '',
          scopes: ['openid', 'profile', 'email'],
          auto_provision: false,
          require_sso: false,
          default_role_id: '',
          is_active: true,
          email_domain: existingSsoDomain
        })
        setProvider('saml')
      }
    }
  }, [isOpen, ssoConfig, reset, existingSsoDomain])

  const createMutation = useMutation(
    (data) => api.post('/sso-configurations', { ...data, organization_id: organizationId }).then(r => r.data),
    {
      onSuccess: (result) => {
        if (result?.success) {
          // Show replacement information if a configuration was replaced
          if (result?.data?.replacedConfigurations && result.data.replacedConfigurations.length > 0) {
            const previousProvider = result.data.previousProvider || 'previous configuration'
            const newProvider = result.data.newProvider || 'new configuration'
            toast.success(
              `SSO configuration created! Replaced: ${previousProvider} → New: ${newProvider}`,
              { duration: 5000 }
            )
            console.log('Replaced SSO configurations:', result.data.replacedConfigurations)
          } else if (result?.data?.gcpCreated) {
            toast.success('SSO configuration saved successfully and organization added to GCP!')
          } else {
            toast.success(result?.message || 'SSO configuration saved successfully!')
          }
          onSuccess && onSuccess()
          onClose()
        } else {
          toast.error(result?.message || 'Failed to save SSO configuration')
        }
      },
      onError: (error) => {
        console.error('Error saving SSO configuration:', error)
        const msg = error.response?.data?.message || 'Failed to save SSO configuration'
        toast.error(msg)
      }
    }
  )

  const updateMutation = useMutation(
    (data) => api.put(`/sso-configurations/${ssoConfig?.id}`, data).then(r => r.data),
    {
      onSuccess: (result) => {
        if (result?.success) {
          // Show appropriate message based on whether GCP tenant was created
          if (result?.data?.gcpCreated) {
            toast.success('SSO configuration updated successfully and organization added to GCP!')
          } else {
            toast.success(result?.message || 'SSO configuration updated successfully!')
          }
          onSuccess && onSuccess()
          onClose()
        } else {
          toast.error(result?.message || 'Failed to update SSO configuration')
        }
      },
      onError: (error) => {
        console.error('Error updating SSO configuration:', error)
        const msg = error.response?.data?.message || 'Failed to update SSO configuration'
        toast.error(msg)
      }
    }
  )

  const testConnectionMutation = useMutation(
    (configId) => api.post(`/sso-configurations/${configId}/test`).then(r => r.data),
    {
      onSuccess: (result) => {
        setTestResult(result)
        setIsTesting(false)
        if (result.success) {
          toast.success('Connection test successful!')
        } else {
          toast.error('Connection test failed')
        }
      },
      onError: () => {
        setTestResult({ success: false, message: 'Connection test failed' })
        setIsTesting(false)
        toast.error('Connection test failed')
      }
    }
  )

  const onSubmit = async (data) => {
    // For OIDC, use the oidc_provider value as the actual provider
    const actualProvider = data.provider === 'oidc' && data.oidc_provider 
      ? data.oidc_provider 
      : data.provider
    
    // Note: We no longer prevent duplicates here because the backend will replace
    // all existing SSO configurations when creating a new one. This allows users
    // to replace existing configurations (e.g., replace email/password with a new email/password)

    const isEmailPassword = actualProvider === 'email_password'
    const submitDataBase = {
      ...data,
      provider: actualProvider, // Use the actual provider (oidc_provider for OIDC, or 'saml')
      scopes: Array.isArray(data.scopes) ? data.scopes : (data.scopes ? data.scopes.split(',').map(s => s.trim()) : ['openid', 'profile', 'email']),
      // empty default role should be null for UUID column
      default_role_id: data.default_role_id ? data.default_role_id : null,
      email_domain: isEmailPassword ? undefined : (data.email_domain || existingSsoDomain || '').trim().toLowerCase()
    }

    // Normalize provider-specific fields so DB types match
    let submitData
    if (submitDataBase.provider === 'saml') {
      submitData = {
        ...submitDataBase,
        client_id: null,
        client_secret: null,
        issuer_url: null,
        password: null,
        confirm_password: null
      }
    } else if (submitDataBase.provider === 'email_password') {
      // For email/password, send password but not client_id/client_secret
      submitData = {
        ...submitDataBase,
        entity_id: null,
        sso_url: null,
        certificate: null,
        client_id: null,
        client_secret: null,
        issuer_url: null,
        scopes: null,
        confirm_password: null // Don't send confirm_password to backend
      }
    } else {
      // For other OIDC providers (google, microsoft, azure), send client_id/client_secret but not password
      submitData = {
        ...submitDataBase,
        entity_id: null,
        sso_url: null,
        certificate: null,
        password: null,
        confirm_password: null
      }
    }

    // Remove oidc_provider from submit data as it's not a DB field
    delete submitData.oidc_provider
    if (isEmailPassword) {
      delete submitData.email_domain
    }

    if (ssoConfig?.id) {
      await updateMutation.mutateAsync(submitData)
    } else {
      await createMutation.mutateAsync(submitData)
    }
  }

  const handleTestConnection = async () => {
    if (!ssoConfig?.id) {
      toast.error('Please save the configuration first before testing')
      return
    }

    setIsTesting(true)
    setTestResult(null)
    await testConnectionMutation.mutateAsync(ssoConfig.id)
  }

  const handleClose = () => {
    if (!createMutation.isLoading && !updateMutation.isLoading) {
      reset()
      setTestResult(null)
      onClose()
    }
  }

  const watchedProvider = watch('provider')
  const watchedOidcProvider = watch('oidc_provider')
  const needsEmailDomain = watchedProvider === 'saml' ||
    (watchedProvider === 'oidc' && watchedOidcProvider && watchedOidcProvider !== 'email_password')

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div className="flex items-center space-x-2">
            <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <Shield className="h-4 w-4 text-blue-600" />
            </div>
            <CardTitle>
              {ssoConfig?.id ? 'Edit SSO Configuration' : 'Configure SSO'}
            </CardTitle>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            disabled={createMutation.isLoading || updateMutation.isLoading}
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Provider Selection */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                SSO Provider *
              </label>
              <div className="grid grid-cols-2 gap-4">
                <label className={`relative flex items-center p-4 border rounded-lg cursor-pointer ${
                  watchedProvider === 'saml' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                }`}>
                  <input
                    {...register('provider')}
                    type="radio"
                    value="saml"
                    className="sr-only"
                  />
                  <div className="flex items-center">
                    <Shield className="h-5 w-5 mr-3 text-blue-600" />
                    <div>
                      <div className="font-medium">SAML 2.0</div>
                      <div className="text-sm text-gray-500">Enterprise SSO</div>
                    </div>
                  </div>
                </label>
                <label className={`relative flex items-center p-4 border rounded-lg cursor-pointer ${
                  watchedProvider === 'oidc' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                }`}>
                  <input
                    {...register('provider')}
                    type="radio"
                    value="oidc"
                    className="sr-only"
                  />
                  <div className="flex items-center">
                    <Shield className="h-5 w-5 mr-3 text-blue-600" />
                    <div>
                      <div className="font-medium">OpenID Connect</div>
                      <div className="text-sm text-gray-500">OAuth 2.0 / OIDC</div>
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {needsEmailDomain && (
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Email Domain *
                </label>
                <input
                  {...register('email_domain', {
                    required: needsEmailDomain && !isDomainLocked ? 'Email domain is required' : false,
                    validate: (value) => {
                      if (!needsEmailDomain || isDomainLocked) return true
                      const domain = (value || '').trim().toLowerCase()
                      const domainPattern = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)*\.[a-z]{2,}$/
                      return domainPattern.test(domain) || 'Please enter a valid email domain (e.g., comptivia.com)'
                    }
                  })}
                  type="text"
                  className="input w-full"
                  placeholder="comptivia.com"
                  disabled={createMutation.isLoading || updateMutation.isLoading || isDomainLocked}
                  readOnly={isDomainLocked}
                />
                {errors.email_domain && (
                  <p className="mt-1 text-sm text-red-600">{errors.email_domain.message}</p>
                )}
                <p className="mt-1 text-xs text-secondary-500">
                  {isDomainLocked
                    ? 'SSO email domain is already set for this organization and cannot be changed.'
                    : 'Domain used for SSO (e.g., comptivia.com)'}
                </p>
              </div>
            )}

            {/* SAML Configuration */}
            {watchedProvider === 'saml' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-secondary-900">SAML Configuration</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Entity ID *
                    </label>
                    <input
                      {...register('entity_id', { required: 'Entity ID is required' })}
                      type="text"
                      className="input w-full"
                      placeholder="https://your-domain.com/saml/metadata"
                      disabled={createMutation.isLoading || updateMutation.isLoading}
                    />
                    {errors.entity_id && (
                      <p className="mt-1 text-sm text-red-600">{errors.entity_id.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      SSO URL *
                    </label>
                    <input
                      {...register('sso_url', { required: 'SSO URL is required' })}
                      type="url"
                      className="input w-full"
                      placeholder="https://your-domain.com/saml/sso"
                      disabled={createMutation.isLoading || updateMutation.isLoading}
                    />
                    {errors.sso_url && (
                      <p className="mt-1 text-sm text-red-600">{errors.sso_url.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Certificate *
                  </label>
                  <textarea
                    {...register('certificate', { required: 'Certificate is required' })}
                    rows={6}
                    className="input w-full resize-none"
                    placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                    disabled={createMutation.isLoading || updateMutation.isLoading}
                  />
                  {errors.certificate && (
                    <p className="mt-1 text-sm text-red-600">{errors.certificate.message}</p>
                  )}
                </div>
              </div>
            )}

            {/* OIDC Configuration */}
            {watchedProvider === 'oidc' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-secondary-900">OpenID Connect Configuration</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Provider *
                    </label>
                    <select
                      {...register('oidc_provider', { required: 'Provider is required' })}
                      className="input w-full"
                      disabled={createMutation.isLoading || updateMutation.isLoading}
                    >
                      <option value="">Select provider</option>
                      <option value="email_password">Email/Password</option>
                      <option value="google">Google</option>
                      <option value="azure">Azure</option>
                      <option value="microsoft">Microsoft</option>
                    </select>
                    {errors.oidc_provider && (
                      <p className="mt-1 text-sm text-red-600">{errors.oidc_provider.message}</p>
                    )}
                  </div>

                  {/* Show Password and Confirm Password for email/password provider */}
                  {watchedOidcProvider === 'email_password' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-2">
                          Password *
                        </label>
                        <input
                          {...register('password', {
                            required: watchedOidcProvider === 'email_password' ? 'Password is required' : false,
                            minLength: {
                              value: 8,
                              message: 'Password must be at least 8 characters'
                            }
                          })}
                          type="password"
                          className="input w-full"
                          placeholder="Enter Password"
                          disabled={createMutation.isLoading || updateMutation.isLoading}
                        />
                        {errors.password && (
                          <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-2">
                          Confirm Password *
                        </label>
                        <input
                          {...register('confirm_password', {
                            required: watchedOidcProvider === 'email_password' ? 'Please confirm your password' : false,
                            validate: value => value === watch('password') || 'Passwords do not match'
                          })}
                          type="password"
                          className="input w-full"
                          placeholder="Confirm Password"
                          disabled={createMutation.isLoading || updateMutation.isLoading}
                        />
                        {errors.confirm_password && (
                          <p className="mt-1 text-sm text-red-600">{errors.confirm_password.message}</p>
                        )}
                      </div>
                    </>
                  )}

                  {/* Show SSO Client ID and Secret for SSO providers (not email/password) */}
                  {watchedOidcProvider && watchedOidcProvider !== 'email_password' && watchedOidcProvider !== '' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-2">
                          Client ID *
                        </label>
                        <input
                          {...register('client_id', { 
                            required: watchedOidcProvider && watchedOidcProvider !== 'email_password' ? 'Client ID is required' : false
                          })}
                          type="text"
                          className="input w-full"
                          placeholder="your-client-id"
                          disabled={createMutation.isLoading || updateMutation.isLoading}
                        />
                        {errors.client_id && (
                          <p className="mt-1 text-sm text-red-600">{errors.client_id.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-2">
                          Client Secret *
                        </label>
                        <input
                          {...register('client_secret', { 
                            required: watchedOidcProvider && watchedOidcProvider !== 'email_password' ? 'Client Secret is required' : false
                          })}
                          type="password"
                          className="input w-full"
                          placeholder="your-client-secret"
                          disabled={createMutation.isLoading || updateMutation.isLoading}
                        />
                        {errors.client_secret && (
                          <p className="mt-1 text-sm text-red-600">{errors.client_secret.message}</p>
                        )}
                      </div>
                    </>
                  )}

                  {/* Show Issuer URL only for non-email/password providers */}
                  {watchedOidcProvider && watchedOidcProvider !== 'email_password' && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-secondary-700 mb-2">
                        Issuer URL *
                      </label>
                      <input
                        {...register('issuer_url', { 
                          required: watchedOidcProvider && watchedOidcProvider !== 'email_password' ? 'Issuer URL is required' : false
                        })}
                        type="url"
                        className="input w-full"
                        placeholder="https://your-domain.com/.well-known/openid_configuration"
                        disabled={createMutation.isLoading || updateMutation.isLoading}
                      />
                      {errors.issuer_url && (
                        <p className="mt-1 text-sm text-red-600">{errors.issuer_url.message}</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Show Scopes only for non-email/password providers */}
                {watchedOidcProvider && watchedOidcProvider !== 'email_password' && (
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Scopes
                    </label>
                    <input
                      {...register('scopes')}
                      type="text"
                      className="input w-full"
                      placeholder="openid, profile, email"
                      disabled={createMutation.isLoading || updateMutation.isLoading}
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      Comma-separated list of OAuth scopes
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Advanced Options */}
            {/* <div className="space-y-4">
              <h3 className="text-lg font-medium text-secondary-900">Advanced Options</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Default Role
                  </label>
                  <select
                    {...register('default_role_id')}
                    className="input w-full"
                    disabled={createMutation.isLoading || updateMutation.isLoading}
                  >
                    <option value="">Select default role</option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-3">
                  <label className="flex items-center">
                    <input
                      {...register('auto_provision')}
                      type="checkbox"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      disabled={createMutation.isLoading || updateMutation.isLoading}
                    />
                    <span className="ml-2 text-sm text-secondary-700">
                      Auto-provision new users
                    </span>
                  </label>

                  <label className="flex items-center">
                    <input
                      {...register('require_sso')}
                      type="checkbox"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      disabled={createMutation.isLoading || updateMutation.isLoading}
                    />
                    <span className="ml-2 text-secondary-700">
                      Require SSO for all users
                    </span>
                  </label>

                  <label className="flex items-center">
                    <input
                      {...register('is_active')}
                      type="checkbox"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      disabled={createMutation.isLoading || updateMutation.isLoading}
                    />
                    <span className="ml-2 text-sm text-secondary-700">
                      Configuration is active
                    </span>
                  </label>
                </div>
              </div>
            </div> */}

            {/* Test Connection */}
            {ssoConfig?.id && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-secondary-900">Test Connection</h3>
                
                <div className="flex items-center space-x-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleTestConnection}
                    disabled={isTesting || createMutation.isLoading || updateMutation.isLoading}
                  >
                    {isTesting ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      <TestTube className="h-4 w-4 mr-2" />
                    )}
                    Test Connection
                  </Button>

                  {testResult && (
                    <div className={`flex items-center space-x-2 ${
                      testResult.success ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {testResult.success ? (
                        <CheckCircle className="h-5 w-5" />
                      ) : (
                        <AlertCircle className="h-5 w-5" />
                      )}
                      <span className="text-sm font-medium">
                        {testResult.success ? 'Connection successful' : 'Connection failed'}
                      </span>
                    </div>
                  )}
                </div>

                {testResult && (
                  <div className={`p-4 rounded-lg ${
                    testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                  }`}>
                    <p className="text-sm">
                      {testResult.message}
                    </p>
                    {testResult.responseTime && (
                      <p className="text-xs mt-1 opacity-75">
                        Response time: {testResult.responseTime}ms
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-secondary-200">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={createMutation.isLoading || updateMutation.isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createMutation.isLoading || updateMutation.isLoading}
                className="min-w-[120px]"
              >
                {createMutation.isLoading || updateMutation.isLoading ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  ssoConfig?.id ? 'Update Configuration' : 'Save Configuration'
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}


